import { Table, TableWrapper, Tbody, Td, Tr } from '@components/core-ui/Table';
import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import TrashBin from '@components/icons/TrashBin.svg';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useQuery } from '@tanstack/react-query';
import { WorkspaceWithStatusesResponseDto } from 'app/API';
import { workspacesKeys } from 'app/queries/workspaces/workspacesKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createFetchOrganizationWorkspacesQueryFn } from '../api/fetchOrganizationWorkspaces';
import { LoadError } from '../LoadError';
import { shouldRefetchWorkspaces } from './helpers/shouldRefetchWorkspaces';
import { CellContent } from './styles/CellContent';
import { LastCellContent } from './styles/LastCellContent';
import { Wrapper } from './styles/Wrapper';

const EMPTY_WORKSPACES: WorkspaceWithStatusesResponseDto[] = [];

export const WorkspaceTable = () => {
  const { call } = useAPI();
  const [workspacesDisclosureOpen, setWorkspacesDisclosureOpen] = useState<boolean>(true);
  const { openModal } = useModalContext();
  const { organization, notificationsReceiveDate } = useAuthContext();
  const organizationId = organization?.id ?? '';

  const workspacesQuery = useQuery({
    queryKey: workspacesKeys.listsByOrganization(organizationId),
    queryFn: createFetchOrganizationWorkspacesQueryFn(call),
    enabled: organizationId !== '',
  });
  const workspaces = workspacesQuery.data ?? EMPTY_WORKSPACES;

  const workspaceSectionTitle = `Workspaces${workspaces.length > 0 ? ` (${workspaces.length})` : ''}`;

  const currentWorkspaceIds = useMemo(() => {
    if (!organization?.workspaces) {
      return '';
    }
    return organization.workspaces
      .map((ws) => ws.id)
      .sort()
      .join(',');
  }, [organization?.workspaces]);

  const refetchTriggersRef = useRef({ workspaceIds: currentWorkspaceIds, notificationsReceiveDate });

  useEffect(() => {
    const previous = refetchTriggersRef.current;
    refetchTriggersRef.current = { workspaceIds: currentWorkspaceIds, notificationsReceiveDate };

    const shouldRefetch = shouldRefetchWorkspaces({
      workspaceListChanged: previous.workspaceIds !== currentWorkspaceIds,
      notificationsReceived: previous.notificationsReceiveDate !== notificationsReceiveDate,
      msSinceLastFetch: workspacesQuery.isFetching ? 0 : Date.now() - workspacesQuery.dataUpdatedAt,
    });

    if (shouldRefetch) {
      void workspacesQuery.refetch();
    }
  }, [currentWorkspaceIds, notificationsReceiveDate]);

  const handleCreateWorkspace = () => {
    openModal(ModalNamesEnum.NewWorkspaceModal);
  };

  const handleDeleteWorkspace = (workspace: WorkspaceWithStatusesResponseDto) => {
    openModal(ModalNamesEnum.DeleteWorkspaceModal, { workspace });
  };

  return (
    <Wrapper className="flex flex-col">
      <div>
        <DisclosureSection
          title={workspaceSectionTitle}
          open={workspacesDisclosureOpen}
          setOpenState={setWorkspacesDisclosureOpen}
          loading={workspacesQuery.isLoading}
        >
          {workspacesQuery.isError ? (
            <LoadError message="Failed to load workspaces." onRetry={() => void workspacesQuery.refetch()} />
          ) : null}
          <TableWrapper>
            <Table>
              <Tbody>
                {workspaces.map((data) => {
                  const hasJobs = data.jobsProcessing + data.jobsReadyForTranscribing + data.jobsComplete > 0;
                  return (
                    <Tr key={data.id}>
                      <Td>
                        <CellContent style={{ width: '250px' }}>
                          <Link to={routes.workspaceDetails.make(organization?.slug, data.slug)}>{data.name}</Link>
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <span style={{ display: 'block' }}>{data.jobsProcessing} jobs processing</span>
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <span style={{ display: 'block' }}>
                            {data.jobsReadyForTranscribing} jobs ready for editing
                          </span>
                        </CellContent>
                      </Td>
                      <Td style={{ width: '1%' }}>
                        <CellContent>
                          <span style={{ display: 'block' }}>{data.jobsComplete} completed jobs</span>
                        </CellContent>
                      </Td>
                      <Td style={{ width: '1%' }}>
                        <LastCellContent>
                          {data.slug !== 'asone-realtime' && organization?.permissions?.manageWorkspaces && !hasJobs ? (
                            <TrashBin
                              className="hover:text-[var(--color-title)] text-[var(--color-text-muted)] cursor-pointer"
                              onClick={() => handleDeleteWorkspace(data)}
                            />
                          ) : null}
                        </LastCellContent>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableWrapper>
          <div className="grid place-items-end">
            <div className="w-36">
              <OutlinedButton className="my-2 w-[140px]" disabled={false} onClick={handleCreateWorkspace}>
                Create Workspace
              </OutlinedButton>
            </div>
          </div>
        </DisclosureSection>
      </div>
    </Wrapper>
  );
};
